// Conversation thread switcher — three-tier lifecycle:
//   Active   → conversations in use (main section)
//   Recent   → completed but not archived (collapsible, dimmed)
//   Archived → in .archive/ on disk (collapsible, restore-only)
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArchiveRestore,
  Check,
  ChevronDown,
  ChevronRight,
  CircleCheckBig,
  MessageSquarePlus,
  Pencil,
  RotateCcw,
} from 'lucide-react';
import { memo, useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@onestone/ui/dropdown-menu';
import { api } from '@/lib/api';
import { chatStore, useChatConversationId, type ConversationSummary } from '@/lib/chatStore';
import { cn } from '@onestone/ui/utils';
import { ArchiveConversationDialog } from './ArchiveConversationDialog';
import { RenameConversationDialog } from './RenameConversationDialog';
import { LoadingState } from '@onestone/ui/loading-state';
import type { ArchivedSessionListResponse, SessionListResponse } from '@onestone/shared/responses';

const ACTIVE_KEY = ['chat', 'sessions'] as const;
const ARCHIVED_KEY = ['chat', 'sessions', 'archived'] as const;

// memo + the conversation-id hook: ChatPanel re-renders on every chat update
// (each streamed piece of a reply); this menu depends only on which
// conversation is open, so it re-renders only when that changes.
export const ConversationSwitcher = memo(function ConversationSwitcher() {
  const queryClient = useQueryClient();
  const conversationId = useChatConversationId();
  const [archiveTarget, setArchiveTarget] = useState<ConversationSummary | null>(null);
  const [renameTarget, setRenameTarget] = useState<ConversationSummary | null>(null);
  const [showRecent, setShowRecent] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  // Controlled: the conversation entries are plain buttons, not menu items, so
  // Radix doesn't close the menu when one is picked — `pick` does.
  const [menuOpen, setMenuOpen] = useState(false);
  const pick = (id: string): void => {
    setMenuOpen(false);
    void chatStore.switchConversation(id);
  };

  const query = useQuery({
    queryKey: ACTIVE_KEY,
    queryFn: () => api<SessionListResponse>('/api/chat/sessions'),
  });
  const allConversations = query.data?.sessions ?? [];

  // Split into active and recent (completed) — single API, two buckets.
  const activeConversations = allConversations.filter((c) => c.status !== 'completed');
  const recentConversations = allConversations.filter((c) => c.status === 'completed');

  const archivedQuery = useQuery({
    queryKey: ARCHIVED_KEY,
    queryFn: () => api<ArchivedSessionListResponse>('/api/chat/sessions/archived'),
    enabled: showArchived,
  });
  const archived = archivedQuery.data?.sessions ?? [];

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ACTIVE_KEY });
    void queryClient.invalidateQueries({ queryKey: ARCHIVED_KEY });
  };

  // Mark completed — moves from Active to Recent section.
  const completeMutation = useMutation({
    mutationFn: (id: string) =>
      api(`/api/chat/sessions/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        json: { status: 'completed' },
      }),
    onSuccess: (_res, completedId) => {
      refresh();
      if (completedId === conversationId) chatStore.newConversation();
    },
  });

  // Reactivate — moves from Recent back to Active section.
  const reactivateMutation = useMutation({
    mutationFn: (id: string) =>
      api(`/api/chat/sessions/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        json: { status: 'active' },
      }),
    onSuccess: refresh,
  });

  // Archives the conversation (server renames its dir into .archive/ — non-destructive).
  const archiveMutation = useMutation({
    mutationFn: (id: string) => api(`/api/chat/sessions/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: (_res, archivedId) => {
      refresh();
      if (archivedId === conversationId) chatStore.newConversation();
    },
  });

  // Deletes into the OneStone trash (restorable from Settings → Trash).
  const trashMutation = useMutation({
    mutationFn: (id: string) => api(`/api/chat/sessions/${encodeURIComponent(id)}/trash`, { method: 'POST' }),
    onSuccess: (_res, trashedId) => {
      refresh();
      void queryClient.invalidateQueries({ queryKey: ['trash'] });
      if (trashedId === conversationId) chatStore.newConversation();
    },
  });

  // Restores from .archive/ back to the active list.
  const restoreMutation = useMutation({
    mutationFn: (id: string) => api(`/api/chat/sessions/${encodeURIComponent(id)}/restore`, { method: 'POST' }),
    onSuccess: refresh,
  });

  const activeConv = allConversations.find((c) => c.id === conversationId);
  const activeTitle = activeConv?.title ?? activeConv?.model ?? 'Chat';

  const handleRename = (conv: ConversationSummary): void => {
    setRenameTarget(conv);
  };

  const handleArchive = (conv: ConversationSummary): void => {
    setArchiveTarget(conv);
  };

  return (
    <>
      <DropdownMenu
        open={menuOpen}
        onOpenChange={(open) => {
          setMenuOpen(open);
          if (open) refresh();
        }}
      >
        <DropdownMenuTrigger
          data-testid="conversation-switcher"
          className="flex max-w-[220px] items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs font-medium text-foreground hover:bg-muted"
        >
          <span className="truncate">{activeTitle}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-80">
          {/* ── Active conversations ── */}
          <DropdownMenuLabel>Active</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <div className="max-h-72 overflow-y-auto">
            {activeConversations.length === 0 ? (
              <div className="px-2 py-1.5 text-xs text-muted-foreground">No active conversations.</div>
            ) : (
              activeConversations.map((conv) => (
                <div
                  key={conv.id}
                  className={cn(
                    'group flex items-center gap-1 rounded-sm px-2 py-1.5 text-sm hover:bg-muted',
                    conv.id === conversationId && 'bg-muted/60',
                  )}
                >
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
                    onClick={() => pick(conv.id)}
                    title={conv.title ?? conv.model}
                  >
                    <Check
                      className={cn(
                        'h-3.5 w-3.5 shrink-0',
                        conv.id === conversationId ? 'text-primary' : 'text-transparent',
                      )}
                    />
                    <span className="truncate">{conv.title ?? conv.model ?? conv.id.slice(0, 8)}</span>
                    <span className="ml-auto text-xs text-muted-foreground">{conv.messageCount}m</span>
                  </button>
                  <button
                    type="button"
                    className="shrink-0 rounded p-1 text-muted-foreground opacity-0 hover:text-foreground group-hover:opacity-100 touch:opacity-100"
                    onClick={() => handleRename(conv)}
                    title="Rename"
                    aria-label="Rename conversation"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    className="shrink-0 rounded p-1 text-muted-foreground opacity-0 hover:text-foreground group-hover:opacity-100 touch:opacity-100"
                    onClick={() => completeMutation.mutate(conv.id)}
                    title="Mark as done (moves to Recent)"
                    aria-label="Mark conversation as done"
                  >
                    <CircleCheckBig className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))
            )}
          </div>

          {/* ── Recent (completed) conversations ── */}
          {recentConversations.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <button
                type="button"
                className="flex w-full items-center gap-1.5 rounded-sm px-2 py-1.5 text-left text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                onClick={(e) => {
                  e.preventDefault();
                  setShowRecent((v) => !v);
                }}
                aria-expanded={showRecent}
              >
                <ChevronRight
                  className={cn(
                    'h-3.5 w-3.5 shrink-0 transition-transform motion-reduce:transition-none',
                    showRecent && 'rotate-90',
                  )}
                  aria-hidden="true"
                />
                <span>Recent ({recentConversations.length})</span>
              </button>
              {showRecent && (
                <div className="max-h-60 overflow-y-auto border-t border-border/60">
                  {recentConversations.map((conv) => (
                    <div
                      key={conv.id}
                      className="group flex items-center gap-1 rounded-sm px-2 py-1.5 text-sm hover:bg-muted"
                    >
                      <button
                        type="button"
                        className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
                        onClick={() => pick(conv.id)}
                        title={conv.title ?? conv.model}
                      >
                        <span className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                        <span className="truncate text-muted-foreground">
                          {conv.title ?? conv.model ?? conv.id.slice(0, 8)}
                        </span>
                        <span className="ml-auto text-xs text-muted-foreground">{conv.messageCount}m</span>
                      </button>
                      <button
                        type="button"
                        className="shrink-0 rounded p-1 text-muted-foreground opacity-0 hover:text-foreground group-hover:opacity-100 touch:opacity-100"
                        onClick={() => reactivateMutation.mutate(conv.id)}
                        title="Move back to Active"
                        aria-label="Reactivate conversation"
                        disabled={reactivateMutation.isPending}
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        className="shrink-0 rounded p-1 text-muted-foreground opacity-0 hover:text-foreground group-hover:opacity-100 touch:opacity-100"
                        onClick={() => handleArchive(conv)}
                        title="Archive (preserves on disk; recoverable)"
                        aria-label="Archive conversation"
                      >
                        <Archive className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ── Archived conversations ── */}
          <DropdownMenuSeparator />
          <button
            type="button"
            className="flex w-full items-center gap-1.5 rounded-sm px-2 py-1.5 text-left text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
            onClick={(e) => {
              e.preventDefault();
              setShowArchived((v) => !v);
            }}
            aria-expanded={showArchived}
          >
            <ChevronRight
              className={cn(
                'h-3.5 w-3.5 shrink-0 transition-transform motion-reduce:transition-none',
                showArchived && 'rotate-90',
              )}
              aria-hidden="true"
            />
            <span>Archived{archived.length > 0 ? ` (${archived.length})` : ''}</span>
          </button>
          {showArchived ? (
            <div className="max-h-60 overflow-y-auto border-t border-border/60">
              {archivedQuery.isLoading ? (
                <LoadingState className="px-2 py-1.5" />
              ) : archived.length === 0 ? (
                <div className="px-2 py-1.5 text-xs text-muted-foreground">Nothing archived yet.</div>
              ) : (
                archived.map((conv) => (
                  <div
                    key={conv.id}
                    className="group flex items-center gap-1 rounded-sm px-2 py-1.5 text-sm hover:bg-muted"
                  >
                    <span className="flex min-w-0 flex-1 items-center gap-1.5 text-left">
                      <span className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                      <span className="truncate text-muted-foreground">
                        {conv.title ?? conv.model ?? conv.id.slice(0, 8)}
                      </span>
                    </span>
                    <button
                      type="button"
                      className="shrink-0 rounded p-1 text-muted-foreground opacity-0 hover:text-foreground group-hover:opacity-100 touch:opacity-100"
                      onClick={() => restoreMutation.mutate(conv.id)}
                      title="Restore to active list"
                      aria-label="Restore conversation"
                      disabled={restoreMutation.isPending}
                    >
                      <ArchiveRestore className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))
              )}
            </div>
          ) : null}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => chatStore.newConversation()} className="gap-1.5 text-sm font-medium">
            <MessageSquarePlus className="h-3.5 w-3.5" />
            New chat
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ArchiveConversationDialog
        conversation={archiveTarget}
        onClose={() => setArchiveTarget(null)}
        onArchive={(id) => archiveMutation.mutate(id)}
        onDelete={(id) => trashMutation.mutate(id)}
      />
      <RenameConversationDialog conversation={renameTarget} onClose={() => setRenameTarget(null)} onRenamed={refresh} />
    </>
  );
});
